import { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import HomeScreen from './components/HomeScreen';
import SavedPage from './components/SavedPage';
import RecommendationsPage from './components/RecommendationsPage';
import CreatePostPage from './components/CreatePostPage';
import MyUploadsPage from './components/MyUploadsPage';
import ReviewPage from './components/ReviewPage';
import AboutPage from './components/AboutPage';
import PrivacyPage from './components/PrivacyPage';
import SiteFooter from './components/SiteFooter';
import SimpleModal from './components/SimpleModal';
import BetaLabel from './components/BetaLabel';
import { useExperiences } from './context/ExperiencesContext';
import { useSaved } from './context/SavedContext';
import { useAuth } from './auth/AuthContext';
import {
  hasSeenIntro,
  markIntroSeen,
  loadView,
  saveView,
  resetHomeFilters
} from './utils/appNavigationState';

const AppContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0d0015 0%, #1a0a2e 30%, #16002e 60%, #0a0a1a 100%);
  color: white;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji', sans-serif;
  overflow-x: hidden;
  position: relative;

  &::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background:
      radial-gradient(ellipse 80% 50% at 20% 20%, rgba(255, 0, 128, 0.15) 0%, transparent 50%),
      radial-gradient(ellipse 60% 40% at 80% 80%, rgba(0, 200, 255, 0.12) 0%, transparent 50%),
      radial-gradient(ellipse 40% 30% at 50% 50%, rgba(255, 230, 0, 0.08) 0%, transparent 50%);
    pointer-events: none;
    z-index: 0;
  }

`;

const IntroOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(ellipse at center, rgba(26, 10, 46, 0.98) 0%, rgba(13, 0, 21, 0.99) 100%);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  text-align: center;
  padding: 20px;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background:
      radial-gradient(circle at 30% 30%, rgba(255, 0, 128, 0.2) 0%, transparent 40%),
      radial-gradient(circle at 70% 70%, rgba(0, 245, 255, 0.15) 0%, transparent 40%);
    animation: pulse 8s ease-in-out infinite;
  }

  @keyframes pulse {
    0%, 100% { transform: scale(1); opacity: 0.5; }
    50% { transform: scale(1.1); opacity: 0.8; }
  }
`;

const IntroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #ff00aa, #00f5ff, #ffee00, #ff00aa);
  background-size: 300% 300%;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 0 40px rgba(255, 0, 170, 0.5), 0 0 80px rgba(0, 245, 255, 0.3);
  animation: gradientShift 4s ease infinite;
  position: relative;
  z-index: 2;
  letter-spacing: -1px;

  @keyframes gradientShift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
`;

const IntroText = styled.p`
  font-size: 1.2rem;
  line-height: 1.7;
  max-width: 500px;
  margin-bottom: 2.5rem;
  color: rgba(255, 255, 255, 0.85);
  position: relative;
  z-index: 2;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
`;

const IntroButton = styled(motion.button)`
  background: linear-gradient(135deg, #ff00aa 0%, #00f5ff 100%);
  border: none;
  padding: 16px 40px;
  border-radius: 50px;
  color: white;
  font-size: 1.15rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow:
    0 0 20px rgba(255, 0, 170, 0.5),
    0 0 40px rgba(0, 245, 255, 0.3),
    0 10px 40px rgba(0, 0, 0, 0.3);
  position: relative;
  z-index: 2;
  text-transform: uppercase;
  letter-spacing: 2px;
  transition: all 0.3s ease;

  &:hover {
    box-shadow:
      0 0 30px rgba(255, 0, 170, 0.7),
      0 0 60px rgba(0, 245, 255, 0.5),
      0 15px 50px rgba(0, 0, 0, 0.4);
  }
`;

function isPrivacyPath() {
  const path = window.location.pathname.replace(/\/$/, '');
  return path === '/privacy';
}

function App() {
  const { user } = useAuth();
  const { experiences, loading, error, canCreatePost, canReview } = useExperiences();
  const { requestSave, isSaved, closeSaveAuthModal } = useSaved();
  const [showIntro, setShowIntro] = useState(() => !hasSeenIntro() && !isPrivacyPath());
  const [view, setView] = useState(() => (isPrivacyPath() ? 'privacy' : loadView()));
  const [selectedExperience, setSelectedExperience] = useState(null);
  const [homeResetKey, setHomeResetKey] = useState(0);

  useEffect(() => {
    if (view === 'privacy') {
      if (!isPrivacyPath()) window.history.pushState({}, '', '/privacy');
      return;
    }
    saveView(view);
    if (isPrivacyPath()) window.history.pushState({}, '', '/');
  }, [view]);

  useEffect(() => {
    const onPop = () => setView(isPrivacyPath() ? 'privacy' : loadView());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const handleDismissIntro = () => {
    markIntroSeen();
    setShowIntro(false);
  };

  // Show modal when clicking an experience
  const handleExperienceClick = (experience) => {
    if (experience?.originalPath) {
      window.location.assign(experience.originalPath);
      return;
    }
    console.log('User clicked:', experience.title);
    setSelectedExperience(experience);
  };

  // Launch the experience (open link)
  const handleLaunch = (experience) => {
    console.log('User launched:', experience.title);
    window.open(experience.url, '_blank', 'noopener,noreferrer');
    closeSaveAuthModal();
    setSelectedExperience(null);
  };

  // Close the modal
  const handleCloseModal = () => {
    closeSaveAuthModal();
    setSelectedExperience(null);
  };

  const handleGoHome = useCallback(() => {
    resetHomeFilters();
    setView('home');
    setSelectedExperience(null);
    closeSaveAuthModal();
    setHomeResetKey((key) => key + 1);
  }, [closeSaveAuthModal]);

  if (showIntro) {
    return (
      <AppContainer>
        <IntroOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, flexWrap: 'wrap' }}>
              <IntroTitle>RealityCheck</IntroTitle>
              <BetaLabel large />
            </div>
            <IntroText>
              Immersive Entertainment starts here
            </IntroText>
            <IntroButton
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleDismissIntro}
            >
              Discover Experiences
            </IntroButton>
            <SiteFooter />
          </motion.div>
        </IntroOverlay>
      </AppContainer>
    );
  }

  const nav = {
    saved: () => setView('saved'),
    recommendations: () => setView('recommendations'),
    create: () => setView('create'),
    myUploads: () => setView('my-uploads'),
    review: () => setView('review'),
    about: () => setView('about')
  };

  let mainContent;
  if (view === 'saved') {
    mainContent = (
      <SavedPage
        onBack={() => setView('home')}
        onExperienceClick={handleExperienceClick}
        onNavigateToSaved={nav.saved}
        onNavigateToRecommendations={nav.recommendations}
        onNavigateToCreate={nav.create}
        onNavigateToMyUploads={nav.myUploads}
        onNavigateToReview={nav.review}
        canCreatePost={canCreatePost}
        canReview={canReview}
      />
    );
  } else if (view === 'recommendations') {
    mainContent = (
      <RecommendationsPage
        onBack={() => setView('home')}
        onExperienceClick={handleExperienceClick}
        onNavigateToSaved={nav.saved}
        onNavigateToRecommendations={nav.recommendations}
        onNavigateToCreate={nav.create}
        onNavigateToMyUploads={nav.myUploads}
        onNavigateToReview={nav.review}
        canCreatePost={canCreatePost}
        canReview={canReview}
      />
    );
  } else if (view === 'create') {
    mainContent = (
      <CreatePostPage
        onBack={() => setView('home')}
        onNavigateToSaved={nav.saved}
        onNavigateToRecommendations={nav.recommendations}
        onNavigateToCreate={nav.create}
        onNavigateToMyUploads={nav.myUploads}
        onNavigateToReview={nav.review}
        canCreatePost={canCreatePost}
        canReview={canReview}
      />
    );
  } else if (view === 'my-uploads') {
    mainContent = (
      <MyUploadsPage
        onBack={() => setView('home')}
        onExperienceClick={handleExperienceClick}
        onNavigateToSaved={nav.saved}
        onNavigateToRecommendations={nav.recommendations}
        onNavigateToCreate={nav.create}
        onNavigateToMyUploads={nav.myUploads}
        onNavigateToReview={nav.review}
        canCreatePost={canCreatePost}
        canReview={canReview}
      />
    );
  } else if (view === 'review') {
    mainContent = (
      <ReviewPage
        onBack={() => setView('home')}
        onExperienceClick={handleExperienceClick}
        onNavigateToSaved={nav.saved}
        onNavigateToRecommendations={nav.recommendations}
        onNavigateToCreate={nav.create}
        onNavigateToMyUploads={nav.myUploads}
        onNavigateToReview={nav.review}
        canCreatePost={canCreatePost}
        canReview={canReview}
      />
    );
  } else if (view === 'privacy') {
    mainContent = (
      <PrivacyPage
        onBack={() => setView('home')}
        onNavigateToSaved={nav.saved}
        onNavigateToRecommendations={nav.recommendations}
        onNavigateToCreate={nav.create}
        onNavigateToMyUploads={nav.myUploads}
        onNavigateToReview={nav.review}
        onNavigateToAbout={nav.about}
        canCreatePost={canCreatePost}
        canReview={canReview}
      />
    );
  } else if (view === 'about') {
    mainContent = (
      <AboutPage
        onBack={() => setView('home')}
        onNavigateToSaved={nav.saved}
        onNavigateToRecommendations={nav.recommendations}
        onNavigateToCreate={nav.create}
        onNavigateToMyUploads={nav.myUploads}
        onNavigateToReview={nav.review}
        onNavigateToAbout={nav.about}
        canCreatePost={canCreatePost}
        canReview={canReview}
      />
    );
  } else {
    mainContent = (
      <HomeScreen
        experiences={experiences}
        loading={loading}
        error={error}
        onExperienceClick={handleExperienceClick}
        onNavigateToSaved={nav.saved}
        onNavigateToRecommendations={nav.recommendations}
        onNavigateToCreate={nav.create}
        onNavigateToMyUploads={nav.myUploads}
        onNavigateToReview={nav.review}
        onNavigateToAbout={nav.about}
        onGoHome={handleGoHome}
        homeResetKey={homeResetKey}
        canCreatePost={canCreatePost}
        canReview={canReview}
      />
    );
  }

  return (
    <AppContainer>
      {mainContent}
      <SiteFooter />
      {selectedExperience && (
        <SimpleModal
          experience={selectedExperience}
          onClose={handleCloseModal}
          onLaunch={handleLaunch}
          onExperienceClick={handleExperienceClick}
          showSaveButton
          isSaved={isSaved(selectedExperience.id)}
          onSaveClick={(anchorEl) => requestSave(selectedExperience.id, anchorEl)}
        />
      )}
    </AppContainer>
  );
}

export default App;
